import { cn } from "@/lib/utils"
import type { StepComponentProps } from "./step-contract"

const NONE_VALUE = "none"

/**
 * Aura multi-select rows (Steps 22, 24): emoji-leading 56px rows with a trailing
 * radio, `main-purple` selected fill, and exclusive “None of these”.
 */
export function AuraMultiChoiceStep({ step, value, onChange }: StepComponentProps) {
  const selected = Array.isArray(value) ? value : []

  const toggle = (optionValue: string) => {
    if (optionValue === NONE_VALUE) {
      onChange(selected.includes(NONE_VALUE) ? [] : [NONE_VALUE])
      return
    }
    const withoutNone = selected.filter((v) => v !== NONE_VALUE)
    if (withoutNone.includes(optionValue)) {
      onChange(withoutNone.filter((v) => v !== optionValue))
    } else if (step.maxSelect == null || withoutNone.length < step.maxSelect) {
      onChange([...withoutNone, optionValue])
    }
  }

  return (
    <div className="flex flex-col gap-2 [@media(max-height:740px)]:gap-1.5" data-slot="aura-multi-choice-step">
      {(step.options ?? []).map((option) => {
        const checked = selected.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggle(option.value)}
            className={cn(
              "flex h-14 w-full items-center gap-3 rounded-xl border-2 px-4 text-left text-base font-semibold backdrop-blur-[15px] transition-colors [@media(max-height:740px)]:h-12",
              checked
                ? "border-main-purple bg-main-purple/10 text-purple-light"
                : "border-transparent bg-purple-light/10 text-purple-light hover:bg-purple-light/15",
            )}
          >
            {option.icon && (
              <span className="flex size-8 shrink-0 items-center justify-center text-[32px] leading-none">
                {option.icon}
              </span>
            )}
            <span className="flex-1">{option.label}</span>
            <span
              className={cn(
                "flex size-6 shrink-0 items-center justify-center rounded-full transition-colors",
                checked
                  ? "bg-gradient-to-b from-peach to-purple-accent"
                  : "border border-purple-light/20",
              )}
            >
              {checked && (
                <svg
                  width="11"
                  height="8"
                  viewBox="0 0 11 8"
                  fill="none"
                  aria-hidden
                  className="text-white"
                >
                  <path
                    d="M1 4L4 7L10 1"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}
